import { useCallback, useMemo, useState } from 'react';

/** 010-1234-5678 / 01012345678 / 1544-3230 형태를 모두 허용 */
const PHONE = /^(0\d{1,2}-?\d{3,4}-?\d{4}|\d{4}-?\d{4})$/;
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

export const validators = {
  required: (msg) => (v) => (String(v ?? '').trim() ? '' : msg),
  checked: (msg) => (v) => (v === true ? '' : msg),
  phone: (msg) => (v) => {
    const raw = String(v ?? '').replace(/\s/g, '');
    if (!raw) return '';
    return PHONE.test(raw) ? '' : msg;
  },
  email: (msg) => (v) => {
    const raw = String(v ?? '').trim();
    if (!raw) return '';
    return EMAIL.test(raw) ? '' : msg;
  },
};

/** 필드별 검사기 배열을 순서대로 적용해 첫 번째 오류 메시지를 남긴다. */
export function buildValidate(rules) {
  return (values) => {
    const errors = {};
    Object.entries(rules).forEach(([name, checks]) => {
      for (const check of checks) {
        const message = check(values[name], values);
        if (message) {
          errors[name] = message;
          return;
        }
      }
    });
    return errors;
  };
}

/**
 * 제어 컴포넌트 폼 상태 + 검증 + 제출 상태.
 * status: 'idle' | 'submitting' | 'success' | 'error'
 */
export default function useForm({ initialValues, validate, onSubmit }) {
  const [values, setValues] = useState(initialValues);
  const [touched, setTouched] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [status, setStatus] = useState('idle');

  const errors = useMemo(() => validate(values), [validate, values]);
  const isValid = Object.keys(errors).length === 0;

  const handleChange = useCallback((e) => {
    const { name, type, value, checked } = e.target;
    setValues((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    // 결과 배너가 떠 있는 동안 다시 입력하면 배너를 지운다.
    setStatus((prev) => (prev === 'success' || prev === 'error' ? 'idle' : prev));
  }, []);

  const handleBlur = useCallback((e) => {
    const { name } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
  }, []);

  /** 아직 건드리지 않은 필드는 제출 전까지 오류를 감춘다. */
  const errorOf = useCallback(
    (name) => ((touched[name] || submitted) && errors[name]) || '',
    [errors, submitted, touched]
  );

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      setSubmitted(true);

      if (!isValid) {
        const first = Object.keys(errors)[0];
        const el = e.target.elements?.[first];
        if (el && typeof el.focus === 'function') el.focus();
        return;
      }

      setStatus('submitting');
      try {
        await onSubmit(values);
        setValues(initialValues);
        setTouched({});
        setSubmitted(false);
        setStatus('success');
      } catch (err) {
        console.error('[offergroup] 폼 제출 실패:', err);
        setStatus('error');
      }
    },
    [errors, initialValues, isValid, onSubmit, values]
  );

  return {
    values,
    errors,
    status,
    isValid,
    submitting: status === 'submitting',
    errorOf,
    handleChange,
    handleBlur,
    handleSubmit,
  };
}
